import type { TooltipContent } from "../render/RenderContext.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { rectContains, type Point, type Rect, type Size } from "../../common/geometry.js";
import { visibleRowRange } from "../core/virtualWindow.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawTriangle } from "../themes/glyphs.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { ComboBox } from "./ComboBox.js";
import {
  cellChoices,
  cellEditText,
  cellText,
  cellValue,
  columnMinWidth,
  columnWidth,
  isEditable,
  parseCell,
  setCellValue,
  sortedOrder,
  type DataGridColumn,
  type SortDirection,
} from "./DataGridColumn.js";
import { drawAlignedText } from "./Label.js";
import { ScrollableBase, type ScrollableOptions } from "./ScrollableBase.js";
import { TextBox } from "./TextBox.js";

export interface DataGridOptions<T = Record<string, unknown>> extends ScrollableOptions {
  columns?: DataGridColumn<T>[];
  rows?: T[];
  rowHeight?: number;
  font?: FontSpec;
  /** The narrow selector strip down the left. */
  showRowHeader?: boolean;
  /** Allow in-place editing at all. Individual columns can still opt out. */
  readOnly?: boolean;
  onCellChanged?: (row: T, column: DataGridColumn<T>) => void;
}

/** How close to a divider counts as grabbing it. */
const DIVIDER_SLOP = 4;
const ROW_HEADER_WIDTH = 26;
const CELL_PADDING = 6;

/** A cell address in *display* coordinates: view row, column index. */
export interface CellAddress {
  row: number;
  column: number;
}

/**
 * An editable, sortable grid bound to an array of objects.
 *
 * Where `ListView` is a read-only report, this binds columns to properties,
 * tracks a current *cell* rather than a row, sorts itself, and edits in place
 * with a real `TextBox` or `ComboBox` floated over the cell — so the editor
 * gets caret, selection, clipboard and IME for free.
 *
 * Sorting permutes a view of row indices rather than reordering the array it
 * was given, so the caller's data is never shuffled underneath them.
 */
export class DataGrid<T = Record<string, unknown>> extends ScrollableBase {
  #columns: DataGridColumn<T>[];
  #rows: T[];
  #view: number[] = [];
  #sortColumn = -1;
  #sortDirection: SortDirection = null;
  #current: CellAddress = { row: -1, column: -1 };
  #hoverRow = -1;
  #rowHeight: number | null;
  #font: FontSpec | null;
  #resizing: { column: number; startX: number; startWidth: number } | null = null;
  #editor: TextBox | ComboBox<string> | null = null;
  #editing: CellAddress | null = null;

  showRowHeader: boolean;
  readOnly: boolean;
  onCellChanged: ((row: T, column: DataGridColumn<T>) => void) | null;

  constructor(options: DataGridOptions<T> = {}) {
    super({ width: 420, height: 220, tabStop: true, ...options });
    this.#columns = options.columns ?? [];
    this.#rows = options.rows ?? [];
    this.#rowHeight = options.rowHeight ?? null;
    this.#font = options.font ?? null;
    this.showRowHeader = options.showRowHeader ?? true;
    this.readOnly = options.readOnly ?? false;
    this.onCellChanged = options.onCellChanged ?? null;
    this.#rebuildView();

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => (this.#resizing = null));
    this.on("pointerleave", () => this.#setHoverRow(-1));
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("blur", () => {
      // Opening an editor focuses it, which blurs the grid. The focus manager
      // has already recorded where focus went, so check: only a move to
      // something outside this grid should commit and close.
      const focused = this.desktop?.focusManager.focused;
      if (focused && this.contains(focused)) return;
      this.commitEdit();
    });
  }

  // --- model ------------------------------------------------------------

  get columns(): DataGridColumn<T>[] {
    return this.#columns;
  }
  set columns(v: DataGridColumn<T>[]) {
    this.#columns = v;
    this.cancelEdit();
    this.#sortColumn = -1;
    this.#sortDirection = null;
    this.#rebuildView();
  }

  get rows(): T[] {
    return this.#rows;
  }
  set rows(v: T[]) {
    this.#rows = v;
    this.cancelEdit();
    this.#current = { row: -1, column: -1 };
    this.#rebuildView();
  }

  get rowCount(): number {
    return this.#view.length;
  }

  /** Source-array index of a display row, or -1. */
  sourceIndex(viewRow: number): number {
    return this.#view[viewRow] ?? -1;
  }

  /** The row object at a display position. */
  rowAt(viewRow: number): T | undefined {
    const i = this.#view[viewRow];
    return i === undefined ? undefined : this.#rows[i];
  }

  get currentCell(): CellAddress {
    return { ...this.#current };
  }
  set currentCell(v: CellAddress) {
    const row = Math.max(-1, Math.min(this.rowCount - 1, v.row));
    const column = Math.max(-1, Math.min(this.#columns.length - 1, v.column));
    if (row === this.#current.row && column === this.#current.column) return;
    this.commitEdit();
    this.#current = { row, column };
    this.invalidate();
    this.emit("selectionchanged");
  }

  get currentRow(): T | undefined {
    return this.rowAt(this.#current.row);
  }

  get sortColumn(): number {
    return this.#sortColumn;
  }

  get sortDirection(): SortDirection {
    return this.#sortDirection;
  }

  /** Sort by a column. Passing null for `direction` restores source order. */
  sortBy(column: number, direction: SortDirection): void {
    this.#sortColumn = direction === null ? -1 : column;
    this.#sortDirection = direction;
    const keep = this.currentRow;
    this.#rebuildView();
    // Follow the row the user was on rather than the position it occupied.
    if (keep !== undefined) {
      const at = this.#view.findIndex((i) => this.#rows[i] === keep);
      if (at >= 0) this.#current = { ...this.#current, row: at };
    }
    this.emit("sortchanged");
  }

  /** Cycle a header through ascending, descending, unsorted. */
  toggleSort(column: number): void {
    const col = this.#columns[column];
    if (!col || col.sortable === false) return;
    if (this.#sortColumn !== column) this.sortBy(column, "asc");
    else if (this.#sortDirection === "asc") this.sortBy(column, "desc");
    else this.sortBy(column, null);
  }

  /** Re-sort and re-measure. Call after mutating rows directly. */
  refresh(): void {
    this.#rebuildView();
  }

  #rebuildView(): void {
    const col = this.#sortColumn >= 0 ? (this.#columns[this.#sortColumn] ?? null) : null;
    this.#view = sortedOrder(this.#rows, col, this.#sortDirection);
    if (this.#current.row >= this.#view.length) this.#current.row = this.#view.length - 1;
    this.updateScrollBars();
    this.clampScroll();
    this.invalidate();
  }

  // --- geometry ---------------------------------------------------------

  get rowHeight(): number {
    return this.#rowHeight ?? this.desktop?.theme.metrics.listItemHeight ?? 24;
  }
  set rowHeight(v: number | null) {
    this.#rowHeight = v;
    this.#rebuildView();
  }

  get headerHeight(): number {
    return this.desktop?.theme.metrics.headerHeight ?? 26;
  }

  get rowHeaderWidth(): number {
    return this.showRowHeader ? ROW_HEADER_WIDTH : 0;
  }

  get totalColumnWidth(): number {
    let w = 0;
    for (const c of this.#columns) w += columnWidth(c);
    return w;
  }

  protected override contentSize(): Size {
    return {
      width: this.rowHeaderWidth + this.totalColumnWidth,
      height: this.headerHeight + this.rowCount * this.rowHeight,
    };
  }

  /** X of a column's left edge, in content coordinates. */
  columnOffset(index: number): number {
    let x = this.rowHeaderWidth;
    for (let i = 0; i < index && i < this.#columns.length; i++) x += columnWidth(this.#columns[i]!);
    return x;
  }

  /** The header strip, in local coordinates. It does not scroll vertically. */
  get headerRect(): Rect {
    const view = this.viewportRect;
    return { x: view.x, y: view.y, width: view.width, height: Math.min(this.headerHeight, view.height) };
  }

  /** A cell's rectangle in local coordinates, scrolling included. */
  cellRect(viewRow: number, column: number): Rect {
    const view = this.viewportRect;
    const col = this.#columns[column];
    return {
      x: view.x + this.columnOffset(column) - this.scrollOffsetX,
      y: view.y + this.headerHeight + viewRow * this.rowHeight - this.scrollOffsetY,
      width: col ? columnWidth(col) : 0,
      height: this.rowHeight,
    };
  }

  /** Display row at a local point, or -1. Excludes the header. */
  rowAtPoint(p: Point): number {
    const view = this.viewportRect;
    if (!rectContains(view, p) || p.y < view.y + this.headerHeight) return -1;
    const y = p.y - view.y - this.headerHeight + this.scrollOffsetY;
    const index = Math.floor(y / this.rowHeight);
    return index >= 0 && index < this.rowCount ? index : -1;
  }

  /** Column at a local point, or -1. Row header counts as -1. */
  columnAtPoint(p: Point): number {
    const view = this.viewportRect;
    const x = p.x - view.x + this.scrollOffsetX;
    if (x < this.rowHeaderWidth) return -1;
    let edge = this.rowHeaderWidth;
    for (let i = 0; i < this.#columns.length; i++) {
      const w = columnWidth(this.#columns[i]!);
      if (x >= edge && x < edge + w) return i;
      edge += w;
    }
    return -1;
  }

  cellAtPoint(p: Point): CellAddress | null {
    const row = this.rowAtPoint(p);
    const column = this.columnAtPoint(p);
    return row >= 0 && column >= 0 ? { row, column } : null;
  }

  /** Column whose right divider is within grabbing distance of `localX`. */
  #dividerAt(localX: number): number {
    const view = this.viewportRect;
    const x = localX - view.x + this.scrollOffsetX;
    let edge = this.rowHeaderWidth;
    for (let i = 0; i < this.#columns.length; i++) {
      edge += columnWidth(this.#columns[i]!);
      if (Math.abs(x - edge) <= DIVIDER_SLOP) return i;
    }
    return -1;
  }

  setColumnWidth(index: number, width: number): void {
    const col = this.#columns[index];
    if (!col) return;
    col.width = Math.max(columnMinWidth(col), Math.round(width));
    this.cancelEdit();
    this.updateScrollBars();
    this.clampScroll();
    this.invalidate();
  }

  /** Widen a column to fit its header and every value in it. */
  autoSizeColumn(index: number): void {
    const col = this.#columns[index];
    const desktop = this.desktop;
    if (!col || !desktop) return;
    const font = this.#font ?? desktop.theme.metrics.font;
    let max = desktop.rc.measureText(col.header, font).width;
    for (const row of this.#rows) {
      const w = desktop.rc.measureText(cellText(col, row), font).width;
      if (w > max) max = w;
    }
    this.setColumnWidth(index, Math.ceil(max) + CELL_PADDING * 2 + 12);
  }

  ensureVisible(cell: CellAddress): void {
    if (cell.row < 0) return;
    const x = this.columnOffset(cell.column);
    const col = this.#columns[cell.column];
    this.scrollIntoView({
      x,
      // The header sits above the rows, so a row's content y excludes it.
      y: cell.row * this.rowHeight,
      width: col ? columnWidth(col) : 0,
      height: this.rowHeight,
    });
  }

  override getTooltip(p: Point): TooltipContent | null {
    const view = this.viewportRect;
    if (p.y < view.y + this.headerHeight) {
      const c = this.columnAtPoint(p);
      return c >= 0 ? (this.#columns[c]!.header ?? null) : this.tooltip;
    }
    const cell = this.cellAtPoint(p);
    if (!cell) return this.tooltip;
    const row = this.rowAt(cell.row);
    const col = this.#columns[cell.column];
    if (!row || !col) return this.tooltip;
    return col.tooltip ? col.tooltip(row) : cellText(col, row);
  }

  // --- editing ----------------------------------------------------------

  get isEditing(): boolean {
    return this.#editing !== null;
  }

  /** The cell being edited, or null. */
  get editingCell(): CellAddress | null {
    return this.#editing ? { ...this.#editing } : null;
  }

  /**
   * The live editor, or null. Exposed so an application can pre-fill or
   * inspect it — the grid's own children also include its scroll bars, so
   * there is no reliable way to find it from outside otherwise.
   */
  get editor(): TextBox | ComboBox<string> | null {
    return this.#editor;
  }

  canEdit(cell: CellAddress): boolean {
    if (this.readOnly || !this.effectivelyEnabled) return false;
    const col = this.#columns[cell.column];
    const row = this.rowAt(cell.row);
    if (!col || row === undefined || !isEditable(col)) return false;
    // A checkbox has no editor; it toggles in place.
    return (col.type ?? "text") !== "check";
  }

  /**
   * Open an editor over a cell.
   *
   * `seed` replaces the current value, which is what makes typing a character
   * start the edit with that character rather than appending to the old text.
   */
  beginEdit(cell: CellAddress, seed?: string): void {
    if (!this.canEdit(cell)) return;
    if (this.#editing) this.commitEdit();
    const col = this.#columns[cell.column]!;
    const row = this.rowAt(cell.row)!;
    this.#current = { ...cell };
    this.ensureVisible(cell);

    const choices = cellChoices(col, row);
    const editor =
      (col.type ?? "text") === "choice"
        ? new ComboBox<string>({
            items: choices,
            editable: col.freeText === true,
            font: this.#font ?? undefined,
          })
        : new TextBox({
            text: seed ?? cellEditText(col, row),
            font: this.#font ?? undefined,
            frame: false,
          });

    if (editor instanceof ComboBox) {
      const current = cellEditText(col, row);
      editor.selectedIndex = choices.indexOf(current);
      if (editor.editable) editor.text = seed ?? current;
    }

    this.#editor = editor;
    this.#editing = { ...cell };
    this.controls.add(editor);
    this.#placeEditor();
    editor.focus();
    if (seed === undefined) editor.selectAll();

    editor.on("keydown", (e) => this.#onEditorKey(e));
    this.invalidate();
  }

  /** Write the editor's value back, if it parses, and close it. */
  commitEdit(): boolean {
    const editing = this.#editing;
    const editor = this.#editor;
    if (!editing || !editor) return true;
    const col = this.#columns[editing.column];
    const row = this.rowAt(editing.row);
    // Close first: a change handler may rebuild the grid underneath us.
    this.#disposeEditor();
    if (!col || row === undefined) return true;

    // Both editors expose the same `text` shape, so this reads either.
    const text = editor.text;
    const parsed = parseCell(col, text, row);
    if (parsed === undefined) {
      // Rejected — a number column asked for a number and got prose.
      this.emit("editrejected");
      this.invalidate();
      return false;
    }
    const before = cellValue(col, row);
    if (before !== parsed) {
      setCellValue(col, row, parsed);
      this.emit("cellchanged");
      this.onCellChanged?.(row, col);
      // Re-sorting on the sorted column would move the row out from under
      // the cursor mid-edit, so only re-derive when it actually matters.
      if (this.#sortColumn === editing.column) this.#rebuildView();
    }
    this.invalidate();
    return true;
  }

  /** Close the editor and keep the original value. */
  cancelEdit(): void {
    if (!this.#editing) return;
    this.#disposeEditor();
    this.invalidate();
  }

  #disposeEditor(): void {
    const editor = this.#editor;
    this.#editor = null;
    this.#editing = null;
    if (!editor) return;
    if (editor instanceof ComboBox) editor.closeDropDown();
    this.controls.remove(editor);
    // Focus went into the editor, so take it back or the grid goes deaf.
    if (this.effectivelyVisible && this.effectivelyEnabled) this.focus();
  }

  #placeEditor(): void {
    const editing = this.#editing;
    const editor = this.#editor;
    if (!editing || !editor) return;
    const r = this.cellRect(editing.row, editing.column);
    const client = this.clientRect;
    // cellRect is in local coordinates; children live inside the client area.
    editor.setBounds(r.x - client.x, r.y - client.y, Math.max(0, r.width), r.height);
  }

  /** Toggle a checkbox cell in place. Returns false if it was not one. */
  toggleCheck(cell: CellAddress): boolean {
    const col = this.#columns[cell.column];
    const row = this.rowAt(cell.row);
    if (!col || row === undefined) return false;
    if ((col.type ?? "text") !== "check" || this.readOnly || !isEditable(col)) return false;
    setCellValue(col, row, !cellValue(col, row));
    this.emit("cellchanged");
    this.onCellChanged?.(row, col);
    if (this.#sortColumn === cell.column) this.#rebuildView();
    this.invalidate();
    return true;
  }

  #onEditorKey(e: KeyEventArgs): void {
    switch (e.key) {
      case "Enter":
        if (this.commitEdit()) this.#moveCurrent(1, 0);
        e.handled = true;
        return;
      case "Escape":
        this.cancelEdit();
        e.handled = true;
        return;
      case "Tab":
        if (this.commitEdit()) this.#moveCurrent(0, e.shiftKey ? -1 : 1);
        e.handled = true;
        return;
      default:
        break;
    }
  }

  // --- input ------------------------------------------------------------

  #setHoverRow(index: number): void {
    if (this.#hoverRow === index) return;
    this.#hoverRow = index;
    this.invalidate();
  }

  override cursorAt(p: Point): string {
    if (this.#resizing) return "col-resize";
    if (rectContains(this.headerRect, p) && this.#dividerAt(p.x) >= 0) return "col-resize";
    return this.cursor;
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (this.#resizing) {
      const delta = e.x - this.#resizing.startX;
      this.setColumnWidth(this.#resizing.column, this.#resizing.startWidth + delta);
      return;
    }
    this.#setHoverRow(this.rowAtPoint({ x: e.x, y: e.y }));
  }

  #onPointerDown(e: MouseEventArgs): void {
    const p = { x: e.x, y: e.y };
    this.focus();

    if (rectContains(this.headerRect, p)) {
      e.handled = true;
      const divider = this.#dividerAt(e.x);
      if (divider >= 0) {
        this.commitEdit();
        const col = this.#columns[divider]!;
        this.#resizing = { column: divider, startX: e.x, startWidth: columnWidth(col) };
        return;
      }
      const column = this.columnAtPoint(p);
      if (column >= 0) this.toggleSort(column);
      return;
    }

    const cell = this.cellAtPoint(p);
    if (!cell) return;
    e.handled = true;

    // A click straight onto the cell that is already current opens the editor,
    // the way a grid lets you correct a value without a round trip.
    const wasCurrent = cell.row === this.#current.row && cell.column === this.#current.column;
    this.currentCell = cell;
    this.ensureVisible(cell);

    const col = this.#columns[cell.column]!;
    if ((col.type ?? "text") === "check") {
      // Only inside the box itself, so clicking the cell merely selects it.
      const r = this.cellRect(cell.row, cell.column);
      if (rectContains(this.#checkRect(r), p)) this.toggleCheck(cell);
      return;
    }
    if (wasCurrent) this.beginEdit(cell);
  }

  #onDoubleClick(e: MouseEventArgs): void {
    const cell = this.cellAtPoint({ x: e.x, y: e.y });
    if (rectContains(this.headerRect, { x: e.x, y: e.y })) {
      const divider = this.#dividerAt(e.x);
      if (divider >= 0) {
        this.autoSizeColumn(divider);
        e.handled = true;
      }
      return;
    }
    if (!cell) return;
    e.handled = true;
    this.beginEdit(cell);
  }

  #moveCurrent(dRow: number, dColumn: number): void {
    if (this.rowCount === 0 || this.#columns.length === 0) return;
    let { row, column } = this.#current;
    if (row < 0) row = 0;
    if (column < 0) column = 0;

    column += dColumn;
    // Tabbing off either end wraps onto the neighboring row, as a grid does.
    if (column >= this.#columns.length) {
      column = 0;
      row += 1;
    } else if (column < 0) {
      column = this.#columns.length - 1;
      row -= 1;
    }
    row += dRow;

    this.currentCell = {
      row: Math.max(0, Math.min(this.rowCount - 1, row)),
      column: Math.max(0, Math.min(this.#columns.length - 1, column)),
    };
    this.ensureVisible(this.#current);
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (this.#editing) return; // the editor has it
    if (e.metaKey || e.altKey) return;
    const cell = this.#current;

    switch (e.key) {
      case "ArrowDown":
        this.#moveCurrent(1, 0);
        e.handled = true;
        return;
      case "ArrowUp":
        this.#moveCurrent(-1, 0);
        e.handled = true;
        return;
      case "ArrowRight":
        this.#moveCurrent(0, 1);
        e.handled = true;
        return;
      case "ArrowLeft":
        this.#moveCurrent(0, -1);
        e.handled = true;
        return;
      case "Tab":
        if (this.rowCount === 0) return;
        this.#moveCurrent(0, e.shiftKey ? -1 : 1);
        e.handled = true;
        return;
      case "Home":
        this.currentCell = { row: e.ctrlKey ? 0 : cell.row, column: 0 };
        this.ensureVisible(this.#current);
        e.handled = true;
        return;
      case "End":
        this.currentCell = {
          row: e.ctrlKey ? this.rowCount - 1 : cell.row,
          column: this.#columns.length - 1,
        };
        this.ensureVisible(this.#current);
        e.handled = true;
        return;
      case "PageDown":
      case "PageUp": {
        const page = Math.max(1, Math.floor((this.viewportRect.height - this.headerHeight) / this.rowHeight) - 1);
        this.#moveCurrent(e.key === "PageDown" ? page : -page, 0);
        e.handled = true;
        return;
      }
      case "F2":
        this.beginEdit(cell);
        e.handled = true;
        return;
      case "Enter":
        if (this.canEdit(cell)) this.beginEdit(cell);
        else this.#moveCurrent(1, 0);
        e.handled = true;
        return;
      case " ":
        if (this.toggleCheck(cell)) e.handled = true;
        return;
      case "Escape":
        return;
      default:
        break;
    }

    // A printable key starts an edit seeded with that character.
    if (e.key.length === 1 && !e.ctrlKey && this.canEdit(cell)) {
      this.beginEdit(cell, e.key);
      e.handled = true;
    }
  }

  // --- painting ---------------------------------------------------------

  /** The checkbox rectangle inside a cell, centered. */
  #checkRect(cell: Rect): Rect {
    const size = this.desktop?.theme.metrics.checkBoxSize ?? 16;
    return {
      x: cell.x + (cell.width - size) / 2,
      y: cell.y + (cell.height - size) / 2,
      width: size,
      height: size,
    };
  }

  protected override onEnvironmentChanged(): void {
    super.onEnvironmentChanged();
    this.#placeEditor();
  }

  protected override performLayout(): void {
    // super first: it is what re-places the scroll bars, and without it they
    // keep whatever geometry they had at the size the grid was built at.
    super.performLayout();
    this.#placeEditor();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    theme.drawListBackground(rc, this.localRect, state);

    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const p = theme.palette;
    const font = this.#font ?? theme.metrics.font;
    const rowH = this.rowHeight;
    const headerH = this.headerHeight;
    const bodyTop = view.y + headerH;
    // Hoisted out of the cell loop: each of these walks to the desktop or
    // allocates, and `cellRect` recomputed every column offset from zero, which
    // made a paint quadratic in the number of columns.
    const focused = this.focused;
    const scrollX = this.scrollOffsetX;
    const viewRight = view.x + view.width;
    const firstColumnX = view.x + this.rowHeaderWidth - scrollX;
    const currentText = focused ? theme.listItemTextColor({ ...state, selected: true, active: true }) : p.text;

    rc.clipped({ x: view.x, y: bodyTop, width: view.width, height: Math.max(0, view.height - headerH) }, () => {
      // The header does not scroll, so it is subtracted from the extent
      // rather than passed as padding.
      const { first, last } = visibleRowRange(
        this.scrollOffsetY,
        view.height - headerH,
        rowH,
        0,
        this.rowCount,
      );

      for (let r = first; r <= last; r++) {
        const row = this.rowAt(r)!;
        const y = bodyTop + r * rowH - this.scrollOffsetY;
        const isCurrentRow = r === this.#current.row;
        const band: Rect = { x: view.x, y, width: view.width, height: rowH };

        if (isCurrentRow || this.#hoverRow === r) {
          theme.drawListItem(rc, band, {
            ...state,
            hovered: this.#hoverRow === r,
            selected: isCurrentRow,
            active: focused,
          });
        }

        if (this.showRowHeader) {
          const rh: Rect = { x: view.x - scrollX, y, width: ROW_HEADER_WIDTH, height: rowH };
          theme.drawColumnHeader(rc, rh, { ...state, hovered: false, pressed: false });
          // The marker Windows grids put beside the current row.
          if (isCurrentRow) {
            drawTriangle(rc, rh, "right", p.text, 3);
          }
        }

        let x = firstColumnX;
        for (let c = 0; c < this.#columns.length; c++) {
          const col = this.#columns[c]!;
          const w = columnWidth(col);
          const left = x;
          x += w;
          if (left > viewRight) break;
          if (left + w < view.x) continue;
          const cell: Rect = { x: left, y, width: w, height: rowH };
          const isCurrent = isCurrentRow && c === this.#current.column;
          const editing = this.#editing?.row === r && this.#editing?.column === c;

          if (isCurrent && !editing) {
            rc.strokeRect(
              { x: cell.x, y: cell.y, width: cell.width - 1, height: cell.height - 1 },
              focused ? p.focusRing : p.controlBorder,
              1,
              0,
            );
          }
          rc.vLine(cell.x + cell.width - 1, cell.y, cell.height, p.gridLine);
          if (editing) continue; // the editor covers it

          if ((col.type ?? "text") === "check") {
            theme.drawCheckBox(rc, this.#checkRect(cell), {
              hovered: false,
              pressed: false,
              focused: false,
              disabled: this.readOnly || !isEditable(col),
              checked: cellValue(col, row) === true,
            });
            continue;
          }

          const textRect: Rect = {
            x: cell.x + CELL_PADDING,
            y: cell.y,
            width: Math.max(0, cell.width - CELL_PADDING * 2),
            height: cell.height,
          };
          drawAlignedText(
            rc,
            cellText(col, row),
            textRect,
            font,
            isCurrentRow ? currentText : p.text,
            col.align ?? ((col.type ?? "text") === "number" ? "right" : "left"),
            "middle",
            true,
          );
        }
        rc.hLine(view.x, y + rowH - 1, view.width, p.gridLine);
      }
    });

    this.#paintHeader(rc, theme, view, font);
  }

  #paintHeader(rc: RenderContext, theme: Theme, view: Rect, font: FontSpec): void {
    const headerH = Math.min(this.headerHeight, view.height);
    if (headerH <= 0) return;
    const p = theme.palette;

    rc.clipped({ x: view.x, y: view.y, width: view.width, height: headerH }, () => {
      if (this.showRowHeader) {
        theme.drawColumnHeader(
          rc,
          { x: view.x - this.scrollOffsetX, y: view.y, width: ROW_HEADER_WIDTH, height: headerH },
          { hovered: false, pressed: false, focused: false, disabled: false },
        );
      }
      let next = view.x + this.rowHeaderWidth - this.scrollOffsetX;
      for (let c = 0; c < this.#columns.length; c++) {
        const col = this.#columns[c]!;
        const x = next;
        const w = columnWidth(col);
        next += w;
        if (x > view.x + view.width) break;
        if (x + w < view.x) continue;
        const cell: Rect = { x, y: view.y, width: w, height: headerH };
        theme.drawColumnHeader(rc, cell, {
          hovered: false,
          pressed: this.#sortColumn === c,
          focused: false,
          disabled: false,
        });

        const sorted = this.#sortColumn === c && this.#sortDirection !== null;
        const arrow = sorted ? 14 : 0;
        drawAlignedText(
          rc,
          col.header,
          {
            x: cell.x + CELL_PADDING,
            y: cell.y,
            width: Math.max(0, cell.width - CELL_PADDING * 2 - arrow),
            height: cell.height,
          },
          font,
          p.headerText,
          col.align ?? "left",
          "middle",
          true,
        );
        if (sorted) {
          drawTriangle(
            rc,
            { x: cell.x + cell.width - arrow - 2, y: cell.y, width: arrow, height: cell.height },
            this.#sortDirection === "asc" ? "up" : "down",
            p.headerText,
            3,
          );
        }
      }
    });
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.paintScrollCorner(rc, theme);
  }

  /**
   * Only the column dividers. Everywhere else a one-finger drag scrolls,
   * which is the whole point of having rows.
   */
  override handlesDragAt(p: Point): boolean {
    return this.cursorAt(p) === "col-resize";
  }
}
